import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import Checkout from "./pages/Checkout";
import Payment from "./pages/Payment";
import Home from "./pages/Home";
import Cart from "./pages/Cart";
import Dashboard from "./pages/Dashboard";
import OrderSuccess from "./pages/OrderSuccess";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProductDetails from "./pages/ProductDetails";
import ReactGA from "react-ga4";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ShippingPolicy from "./pages/ShippingPolicy";
import Contact from "./pages/Contact";

import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminLayout from "./admin/AdminLayout";
import AdminGuard from "./admin/AdminGuard";
import AdminOverview from "./admin/AdminOverview";
import AdminProducts from "./admin/AdminProducts";
import AdminOrders from "./admin/AdminOrders";
import AdminUsers from "./admin/AdminUsers";
import AdminCoupons from "./admin/AdminCoupons";
import AdminSales from "./admin/AdminSales";

function ConditionalNavbar({ cart }) {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return <Navbar cart={cart} />;
}

function SaleBanner({ sale }) {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  if (!sale || dismissed || location.pathname.startsWith("/admin")) return null;

  const handleShopNow = () => {
    // scrolls to the products section on the home page
    window.location.href = "/#products";
  };

  return (
    <div className="tc-sale-banner">
      <div className="tc-sale-banner-track" />
      <div className="tc-sale-banner-glow" />
      <div className="tc-sale-banner-inner">
        <div className="tc-sale-banner-left">
          <div className="tc-sale-dot" />
          <span className="tc-sale-label">Live Sale</span>
          <div className="tc-sale-sep" />
        </div>
        <div className="tc-sale-banner-center">
          <span className="tc-sale-name">{sale.label}</span>
          <span className="tc-sale-pct">— {sale.discountPercent}% OFF —</span>
          <span className="tc-sale-sub">everything in the store</span>
        </div>
        <div className="tc-sale-sep" />
        <div className="tc-sale-banner-right">
          <button className="tc-sale-btn" onClick={handleShopNow}>
            Shop Now →
          </button>
          <button className="tc-sale-dismiss" onClick={() => setDismissed(true)}>
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
function ScrollToTop() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    // Skip scroll-to-top when navigating to a specific section on the home page
    if (!state?.scrollTo) {
      window.scrollTo(0, 0);
    }
  }, [pathname, state]);
  return null;
}

function PageTitle() {
  const location = useLocation();
  useEffect(() => {
    const titles = {
      "/":                 "Tablecure — Your Desk Mate",
      "/cart":             "Your Cart | Tablecure",
      "/checkout":         "Checkout | Tablecure",
      "/payment":          "Secure Payment | Tablecure",
      "/order-success":    "Order Confirmed! | Tablecure",
      "/dashboard":        "My Account | Tablecure",
      "/verify-email":     "Verify Your Email | Tablecure",
      "/forgot-password":  "Forgot Password | Tablecure",
      "/reset-password":   "Reset Password | Tablecure",
      "/privacy-policy":   "Privacy Policy | Tablecure",
      "/terms":            "Terms of Service | Tablecure",
      "/shipping-policy":  "Shipping & Returns | Tablecure",
      "/contact":          "Contact Us | Tablecure",
      "/admin":            "Dashboard | Tablecure Admin",
      "/admin/products":   "Products | Tablecure Admin",
      "/admin/orders":     "Orders | Tablecure Admin",
      "/admin/users":      "Users | Tablecure Admin",
      "/admin/coupons":    "Coupons | Tablecure Admin",
    };
    const title = titles[location.pathname];
    if (title) document.title = title;
  }, [location.pathname]);
  return null;
}
function GAListener() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname,
    });
  }, [location]);

  return null;
}
function App() {
  useEffect(() => {
    ReactGA.initialize("G-VHCMMC3PGS");
  }, []);
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [activeSale, setActiveSale] = useState(null);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/sale/active`)
      .then(r => setActiveSale(r.data || null))
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <GAListener />
      <ScrollToTop />
      <PageTitle />
      <ConditionalNavbar cart={cart} />
      <SaleBanner sale={activeSale} />
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/contact" element={<Contact />} />
      <Route path="/admin" element={
        <AdminGuard>
          <AdminLayout>
            <AdminOverview />
          </AdminLayout>
        </AdminGuard>
      } />
      <Route path="/admin/products" element={
        <AdminGuard><AdminLayout><AdminProducts /></AdminLayout></AdminGuard>
      } />
      <Route path="/admin/orders" element={
        <AdminGuard><AdminLayout><AdminOrders /></AdminLayout></AdminGuard>
      } />
      <Route path="/admin/users" element={
        <AdminGuard><AdminLayout><AdminUsers /></AdminLayout></AdminGuard>
      } />
      <Route path="/admin/coupons" element={
        <AdminGuard><AdminLayout><AdminCoupons /></AdminLayout></AdminGuard>
      } />
      <Route path="/admin/sales" element={
        <AdminGuard><AdminLayout><AdminSales /></AdminLayout></AdminGuard>
      } />
      <Route path="/product/:id" element={<ProductDetails cart={cart} setCart={setCart} />} />
        <Route path="/" element={<Home cart={cart} setCart={setCart} />} />
        <Route path="/cart" element={<Cart cart={cart} setCart={setCart} />} />
        <Route path="/checkout" element={<Checkout cart={cart} setCart={setCart} />} />
        <Route path="/payment" element={<Payment cart={cart} setCart={setCart} />} />
        <Route path="/order-success" element={<OrderSuccess />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </BrowserRouter>
  );
}

export default App;